'use client'

import * as React from 'react'
import ErrorState from './ErrorState'
import { isChunkLoadError, recoverFromChunkError } from '@/lib/chunkError'
import { isAuthError } from '@/lib/authError'

interface ErrorBoundaryProps {
  children: React.ReactNode
  fallback?: (error: Error, reset: () => void) => React.ReactNode
  resetKey?: unknown
}

interface ErrorBoundaryState {
  error: Error | null
}

export default class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  cleanupChunkRecovery: (() => void) | null = null

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (typeof console !== 'undefined') {
      console.error('[ErrorBoundary] Caught error:', error, info)
    }
    // A chunk error can't be reset away (webpack caches the failed promise);
    // reload to re-fetch it once we're online.
    if (isChunkLoadError(error)) {
      this.cleanupChunkRecovery = recoverFromChunkError()
    }
  }

  componentDidMount() {
    // Auto-recover from transient offline errors: when connectivity returns,
    // clear the error so the subtree re-renders instead of staying stuck.
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleOnline)
    }
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.handleOnline)
    }
    this.cleanupChunkRecovery?.()
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.reset()
    }
  }

  handleOnline = () => {
    // Chunk errors are handled by recoverFromChunkError (a reload); only the
    // plain-reset path applies to other transient errors here.
    if (this.state.error && !isChunkLoadError(this.state.error)) {
      this.reset()
    }
  }

  reset = () => {
    this.setState({ error: null })
  }

  handleReset = () => {
    const { error } = this.state
    if (error && isChunkLoadError(error) && typeof window !== 'undefined') {
      window.location.reload()
      return
    }
    this.reset()
  }

  render() {
    const { error } = this.state
    if (error) {
      // Auth errors from the Clerk/Convex sign-out race are recovered by
      // AuthErrorBoundary (spinner + redirect/retry), so bubble them up instead
      // of flashing "Something went wrong" mid-logout.
      if (isAuthError(error)) throw error
      if (this.props.fallback) {
        return this.props.fallback(error, this.handleReset)
      }
      return <ErrorState error={error} reset={this.handleReset} />
    }
    return this.props.children
  }
}
